import { uuid } from '@dittolive/anvil'
import {
  CLIENT_ID,
  rpcApiFindByIdUrl,
  rpcApiWriteUrl,
  rpcLogin,
} from '@portal/core/api'
import axios, { AxiosError } from 'axios'
import { AnonymousProviderConfiguration } from 'components/authenticationWebhooks/useAppAuthentication'
import { SystemCollections, SystemIds } from 'components/dittoAppConnection'
import { PortalError } from 'components/error'
import { withScheme } from 'utils/domainUrl'
import { RpcAuthParams } from 'utils/rpcAuth'

export const PLAYGROUND_AUTH_PROVIDER = '__playgroundProvider'

export const buildNewPlaygroundAuthObject =
  (): AnonymousProviderConfiguration => {
    return {
      type: 'anonymous',
      permission: {
        read: {
          everything: true,
          queriesByCollection: {},
        },
        write: {
          everything: true,
          queriesByCollection: {},
        },
      },
      sessionLength: 604800,
      sharedToken: uuid(),
    }
  }

function retryAsync<T>(
  times: number,
  intervalMs: number,
  fn: () => Promise<T>,
  alias: string,
) {
  const checkFn = async (
    attempts: number,
    resolve: (value?: T) => void,
    reject: (error: Error) => void,
  ) => {
    try {
      const response = await fn()

      return resolve(response)
    } catch (err) {
      // Errors captured while the endpoint is not ready are simply ignored.
    }

    if (attempts < times) {
      setTimeout(() => {
        checkFn(attempts + 1, resolve, reject)
      }, intervalMs)
    } else {
      reject(
        new PortalError(
          'BIG_PEER_CONNECTION_ERROR',
          `Timed out attempting ${alias}.`,
        ),
      )
    }
  }

  return new Promise((resolve, reject) => checkFn(1, resolve, reject))
}

type VerifyParams = {
  /**
   * The number of times to retry the verification before failing.
   */
  retryCount?: number
  /**
   * The interval in milliseconds between each verification attempt.
   */
  intervalMs?: number
}
const defaultVerifyParams: VerifyParams = {
  retryCount: 240,
  intervalMs: 500,
}
/**
 * Verifies an app's subscription server is responsive. Polls the info endpoint
 * for a maximum of 120 seconds until a 200 response is received.
 */
const verifyAppSubscriptionServer = (
  appDomain: string,
  appId: string,
  { retryCount, intervalMs } = defaultVerifyParams,
): Promise<unknown> => {
  const basePath =
    process.env.NODE_ENV === 'development' &&
    !appDomain.includes('dittolive.app/')
      ? `${appDomain}/${appId}`
      : `${appDomain}`
  return retryAsync<unknown>(
    retryCount ?? 240,
    intervalMs ?? 500,
    () => axios.get(`${withScheme(basePath, 'https')}/api/v3/info`),
    'subscription server verification',
  )
}

/** Writes the default auth settings to for the auth_config collection. */
const writeDefaultAuthSettings = async (
  appDomain: string,
  appId: string,
  accessToken: string,
) => {
  await axios.post(
    rpcApiWriteUrl(appDomain, appId),
    {
      commands: [
        {
          method: 'upsert',
          collection: SystemCollections.APP_WEBHOOKS_COLLECTION,
          id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
          value: {
            authenticationEnabled: true,
            providers: { __playgroundProvider: buildNewPlaygroundAuthObject() },
          },
        },
      ],
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-DITTO-CLIENT-ID': CLIENT_ID,
        'X-DITTO-ENSURE-INSERT': 'true',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
}

export const ensureDefaultAuthSettings = async (
  appId: string,
  appDomain: string,
  authParams: RpcAuthParams,
  verifyParams?: VerifyParams,
): Promise<void> => {
  // Make sure the infrastructure is ready before trying to manage the auth config
  await verifyAppSubscriptionServer(appDomain, appId, verifyParams)
  const { accessToken } = await rpcLogin(
    appDomain,
    appId,
    authParams.provider,
    authParams.token,
  )

  try {
    const result = await axios.post(
      rpcApiFindByIdUrl(appDomain, appId),
      {
        collection: SystemCollections.APP_WEBHOOKS_COLLECTION,
        id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      },
      {
        headers: {
          'X-DITTO-CLIENT-ID': CLIENT_ID,
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    if (!result.data?.fields?.providers) {
      // No auth config exists or the auth config is invalid. We should create it
      await writeDefaultAuthSettings(appDomain, appId, accessToken)
      // auth successfully initialized
    }
  } catch (error) {
    const err = error as AxiosError<unknown>
    if (err?.response?.status === 401) {
      // console.log('401 Auth ready')
    } else if (err.response?.status === 404) {
      // No auth exists
      await writeDefaultAuthSettings(appDomain, appId, accessToken)
    } else {
      // console.log('Auth INIT crashed', err)
      throw err
    }
  }
}
